// import { convertValuesToString, keysToCamelCase, keysToSnakeCase, log } from '~/util';
import { keysToCamelCase, keysToSnakeCase } from '~/util/convertKeys';
import { convertValuesToString } from '~/util/convertValues';
import { log } from '~/util/log';

import { APIRequest } from './Request';

interface Options {
  errorOnUnauthorized?: boolean;
  query?: Record<string, any>;
  platformId?: string;
  sandbox?: boolean;
  idempotency?: string;
  reCaptchaToken?: string;
  hideModeHeader?: boolean;
  allowNumbersInRequest?: boolean;
}

export const STEP_UP_REQUIRED_ERROR_CODE = 'step_up_auth_required';

// Registered by the StepUp store (which cannot be imported here without a cycle). When a request is
// rejected with step_up_auth_required, the client awaits this resolver (challenge dialog) and retries once.
let stepUpResolver: (() => Promise<void>) | null = null;

export const setStepUpResolver = (resolver: () => Promise<void>) => {
  stepUpResolver = resolver;
};

class Client {
  #isUnauthorized = false;

  async get<Request, Response>(path: string, options?: Options): Promise<Response> {
    return this.request<Request, Response>(path, 'GET', undefined, options);
  }

  async delete<Request, Response>(path: string, options?: Options): Promise<Response> {
    return this.request<Request, Response>(path, 'DELETE', undefined, options);
  }

  async patch<Request, Response>(path: string, requestBody: Request, options?: Options): Promise<Response> {
    return this.request<Request, Response>(path, 'PATCH', requestBody, options);
  }

  async post<Request, Response>(path: string, requestBody: Request, options?: Options): Promise<Response> {
    return this.request<Request, Response>(path, 'POST', requestBody, options);
  }

  async request<Request, Response>(
    path: string,
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    requestBody?: Request,
    options?: Options,
    isStepUpRetry = false
  ): Promise<Response> {
    // Load the session store lazily to avoid a circular module dependency:
    // Client -> Session -> UserModel -> UserRepository -> Client.
    const { useSessionStore } = await import('~/stores/Session');
    const sessionStore = useSessionStore.getState();

    const data = requestBody
      ? options?.allowNumbersInRequest
        ? JSON.stringify(keysToSnakeCase(requestBody))
        : (JSON.stringify(keysToSnakeCase(convertValuesToString(requestBody))) ?? {})
      : undefined;

    const req = new APIRequest<string, Record<string, any>>(path, method, data, undefined, {
      'Content-Type': 'application/json',
    });

    if (options?.query) {
      req.setQuery(options.query);
    }

    if (options?.idempotency) {
      req.addHeader('Idempotency-Key', options.idempotency);
    }

    if (options?.platformId || sessionStore.currentUser?.defaultPlatformId) {
      req.addHeader('Platform-Id', options?.platformId ?? sessionStore.currentUser?.defaultPlatformId ?? '');
    }

    if (!options?.hideModeHeader) {
      req.addHeader('Live-Mode', (options?.sandbox ?? sessionStore.isSandbox) ? 'No' : 'Yes');
    }

    if (options?.reCaptchaToken && options?.reCaptchaToken?.length > 0) {
      req.addHeader('g-recaptcha-response', options.reCaptchaToken);
    }

    return fetch(req.getRequest())
      .then((response) => {
        if (response.status === 401) {
          if (!this.#isUnauthorized) {
            this.#isUnauthorized = true;
            const currentSessionStore = useSessionStore.getState();

            if (!currentSessionStore.authRequired()) {
              currentSessionStore.clearCookies();
            }

            currentSessionStore.signOut();
          }
          return Promise.reject('Unauthorized');
        }

        return response;
      })
      .then(async (response) => {
        try {
          const json = await response.json();

          if (response.ok) {
            return Promise.resolve(json);
          }

          // !isStepUpRetry bounds this to one retry per approved challenge (a retry that still fails
          // must not re-challenge in a loop). The path guard prevents a deadlock if step-up's own
          // begin/finish ever returned this code: finish would await a challenge whose resolution
          // depends on that same finish call.
          if (
            !isStepUpRetry &&
            stepUpResolver &&
            !path.startsWith('/dashboard/step-up') &&
            (json as { code?: unknown })?.code === STEP_UP_REQUIRED_ERROR_CODE
          ) {
            try {
              await stepUpResolver();
            } catch {
              // Cancelled or failed challenge: surface the original error.
              return Promise.reject(json);
            }
            return this.request<Request, Response>(path, method, requestBody, options, true);
          }

          return Promise.reject(json);
        } catch (message) {
          return Promise.reject(
            JSON.stringify({
              message,
            })
          );
        }
      })
      .then((response) => keysToCamelCase(response))
      .catch((error) => {
        if (
          (typeof error === 'string' && error.trim().startsWith('TypeError: Failed to fetch')) ||
          error instanceof TypeError
        ) {
          log({ name: 'Fetch TypeError', type: 'warn', context: error });
          return;
        }

        if (error !== 'Unauthorized') {
          this.#isUnauthorized = false;
          return Promise.reject(error);
        }

        if (options?.errorOnUnauthorized) {
          return Promise.reject('Unauthorized');
        }

        console.warn('Unauthorized');
      });
  }
}

export const client = new Client();
